// MessageTrendChart — Dashboard「邮件统计」图表（自建 SVG，无图表库）
// 设计取舍：
// - 单序列（每日收件数）→ 不需要图例，标题即序列名；单一品牌色（明 brand-500 / 暗 brand-400），
//   不用第二个紫色深浅当第二序列（两档紫的 normal-vision ΔE≈11.9，肉眼难分）。
// - 形态随点数自适应：≤ 14 天用柱（离散日、零值可见、单日可比），更长窗口用面积折线（趋势优先）。
// - hover 层默认有：整列热区 + 竖向准星 + HTML tooltip（CLAUDE.md §四：不用 native title 当 hint）。
// - 网格/坐标轴走弱化灰，数值文字用文本色不用序列色。
// - **尺寸用 ResizeObserver 实测、按真实像素画**，不靠 viewBox 缩放：
//   固定 viewBox + `preserveAspectRatio` 默认的 meet 会把图按高度缩放后居中，
//   卡片一宽就两侧大片留白，且字号/线宽跟着被拉伸。

import { useEffect, useMemo, useRef, useState } from 'react';

export interface TrendPoint {
  day: string;
  n: number;
}

const PAD_L = 38;
const PAD_R = 14;
const PAD_T = 16;
const PAD_B = 28;
const MIN_PLOT_H = 120;

export function MessageTrendChart({ data }: { data: TrendPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setBox({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stats = useMemo(() => {
    const total = data.reduce((s, d) => s + d.n, 0);
    const peak = data.reduce((m, d) => (d.n > m ? d.n : m), 0);
    const avg = data.length > 0 ? total / data.length : 0;
    return { total, peak, avg };
  }, [data]);

  const W = box.w;
  const H = Math.max(box.h, PAD_T + PAD_B + MIN_PLOT_H);
  const plotW = Math.max(W - PAD_L - PAD_R, 1);
  const plotH = H - PAD_T - PAD_B;
  const baseY = PAD_T + plotH;

  const max = niceMax(stats.peak);
  const asBars = data.length <= 14;
  const band = data.length > 0 ? plotW / data.length : plotW;
  const step = data.length > 1 ? plotW / (data.length - 1) : 0;

  // 柱图取每个 band 中心，折线图取端到端等分
  const xFor = (i: number) => (asBars ? PAD_L + band * (i + 0.5) : PAD_L + step * i);
  const yFor = (n: number) => PAD_T + plotH - (n / max) * plotH;

  const gridVals = [0, max / 2, max];
  const tickIdx = useMemo(() => pickTicks(data.length, plotW), [data.length, plotW]);

  const linePath = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${f(xFor(i))} ${f(yFor(d.n))}`)
    .join(' ');
  const areaPath =
    data.length > 0
      ? `${linePath} L${f(xFor(data.length - 1))} ${f(baseY)} L${f(xFor(0))} ${f(baseY)} Z`
      : '';

  const active = hover != null ? data[hover] : undefined;

  return (
    <div className="flex h-full min-h-[180px] flex-col">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className="whitespace-nowrap">
          合计{' '}
          <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {stats.total.toLocaleString('zh-CN')}
          </span>{' '}
          封
        </span>
        <span className="whitespace-nowrap">
          日均{' '}
          <span className="font-medium tabular-nums text-slate-700 dark:text-slate-200">
            {stats.avg.toFixed(stats.avg >= 10 ? 0 : 1)}
          </span>
        </span>
        <span className="whitespace-nowrap">
          峰值{' '}
          <span className="font-medium tabular-nums text-slate-700 dark:text-slate-200">
            {stats.peak.toLocaleString('zh-CN')}
          </span>
        </span>
      </div>

      <div ref={boxRef} className="relative min-h-[160px] flex-1">
        {W > 0 ? (
          <svg
            width={W}
            height={H}
            viewBox={`0 0 ${String(W)} ${String(H)}`}
            className="block text-brand-500 dark:text-brand-400"
            role="img"
            aria-label={`每日收件数趋势，共 ${String(data.length)} 天，合计 ${String(stats.total)} 封`}
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity={0.28} />
                <stop offset="100%" stopColor="currentColor" stopOpacity={0.02} />
              </linearGradient>
            </defs>

            {/* 网格线 + Y 轴刻度 */}
            {gridVals.map((v) => (
              <g key={v}>
                <line
                  x1={PAD_L}
                  x2={W - PAD_R}
                  y1={yFor(v)}
                  y2={yFor(v)}
                  className="stroke-slate-200 dark:stroke-slate-700"
                  strokeWidth={1}
                  strokeDasharray={v === 0 ? undefined : '3 4'}
                />
                <text
                  x={PAD_L - 8}
                  y={yFor(v) + 4}
                  textAnchor="end"
                  fontSize={11}
                  className="fill-slate-400 dark:fill-slate-500 tabular-nums"
                >
                  {formatTick(v)}
                </text>
              </g>
            ))}

            {/* hover 准星（画在数据层下面，不遮挡） */}
            {hover != null ? (
              <line
                x1={xFor(hover)}
                x2={xFor(hover)}
                y1={PAD_T}
                y2={baseY}
                className="stroke-slate-300 dark:stroke-slate-600"
                strokeWidth={1}
              />
            ) : null}

            {/* 数据层 */}
            {asBars
              ? data.map((d, i) => {
                  const bw = Math.max(6, Math.min(40, band * 0.5));
                  const y = d.n > 0 ? yFor(d.n) : baseY - 2;
                  return (
                    <path
                      key={d.day}
                      d={topRoundedRect(xFor(i) - bw / 2, y, bw, baseY - y, 4)}
                      fill="currentColor"
                      fillOpacity={d.n === 0 ? 0.18 : hover == null || hover === i ? 1 : 0.35}
                      className="transition-[fill-opacity]"
                    />
                  );
                })
              : null}
            {!asBars && data.length > 0 ? (
              <>
                <path d={areaPath} fill="url(#trend-fill)" />
                <path
                  d={linePath}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {hover != null && active ? (
                  <circle
                    cx={xFor(hover)}
                    cy={yFor(active.n)}
                    r={4}
                    fill="currentColor"
                    className="stroke-white dark:stroke-slate-900"
                    strokeWidth={2}
                  />
                ) : null}
              </>
            ) : null}

            {/* 柱顶数值：柱够宽时直接标，省得每个点都要 hover */}
            {asBars && band >= 44
              ? data.map((d, i) =>
                  d.n > 0 ? (
                    <text
                      key={`v-${d.day}`}
                      x={xFor(i)}
                      y={yFor(d.n) - 6}
                      textAnchor="middle"
                      fontSize={11}
                      className="fill-slate-500 dark:fill-slate-400 tabular-nums"
                    >
                      {d.n}
                    </text>
                  ) : null,
                )
              : null}

            {/* X 轴标签 */}
            {data.map((d, i) =>
              tickIdx.has(i) ? (
                <text
                  key={`x-${d.day}`}
                  x={xFor(i)}
                  y={H - 9}
                  textAnchor="middle"
                  fontSize={11}
                  className={
                    hover === i
                      ? 'fill-slate-700 dark:fill-slate-200'
                      : 'fill-slate-400 dark:fill-slate-500'
                  }
                >
                  {i === data.length - 1 ? '今天' : shortDay(d.day)}
                </text>
              ) : null,
            )}

            {/* hover 热区：整列，比柱宽，好点 */}
            {data.map((d, i) => (
              <rect
                key={`hit-${d.day}`}
                x={asBars ? PAD_L + band * i : xFor(i) - step / 2}
                y={PAD_T}
                width={asBars ? band : Math.max(step, 1)}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
            ))}
          </svg>
        ) : null}

        {/* Tooltip（HTML 层，避免跟 SVG 文本混排）
            横向偏到柱的一侧而不是压在柱正上方，否则会盖掉柱顶数值；贴近右边界时翻到左侧 */}
        {hover != null && active && W > 0 ? (
          <div
            className="pointer-events-none absolute top-0 z-10 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800"
            style={{
              left: `${f(xFor(hover))}px`,
              transform:
                xFor(hover) > W * 0.7 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
            }}
          >
            <div className="font-medium whitespace-nowrap text-slate-900 dark:text-slate-100">
              {active.day}
            </div>
            <div className="whitespace-nowrap text-slate-500 dark:text-slate-400">
              收件 <span className="tabular-nums">{active.n.toLocaleString('zh-CN')}</span> 封
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---- helpers ----

/** 顶部圆角、底部贴基线的柱 path */
function topRoundedRect(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return [
    `M${f(x)} ${f(y + h)}`,
    `L${f(x)} ${f(y + rr)}`,
    `Q${f(x)} ${f(y)} ${f(x + rr)} ${f(y)}`,
    `L${f(x + w - rr)} ${f(y)}`,
    `Q${f(x + w)} ${f(y)} ${f(x + w)} ${f(y + rr)}`,
    `L${f(x + w)} ${f(y + h)}`,
    'Z',
  ].join(' ');
}

/** 把峰值抬到 1/2/5×10^k 的「整数刻度」，保证 max/2 也是整数 */
function niceMax(peak: number): number {
  if (peak <= 0) return 4;
  if (peak <= 10) return Math.ceil(peak / 2) * 2;
  const mag = 10 ** Math.floor(Math.log10(peak));
  for (const m of [1, 1.5, 2, 3, 4, 5, 6, 8, 10]) {
    const cand = m * mag;
    if (cand >= peak) return Math.ceil(cand / 2) * 2;
  }
  return Math.ceil(peak / 2) * 2;
}

function formatTick(v: number): string {
  return v >= 1000 ? `${String(Math.round(v / 100) / 10)}k` : String(v);
}

/** '2026-06-15' → '06-15' */
function shortDay(d: string): string {
  const parts = d.split('-');
  if (parts.length < 3) return d;
  return `${parts[1] ?? ''}-${parts[2] ?? ''}`;
}

/**
 * 标签密度：按可用宽度算容量（每个标签留 80px），再取固定步长从最后一天往前数。
 * 用固定步长而不是「均分索引再取整」—— 后者会出现 08-34 / 08-36 这种忽宽忽窄的间距。
 * 从末尾倒数保证「今天」一定有标。
 */
function pickTicks(n: number, plotW: number): Set<number> {
  const set = new Set<number>();
  if (n === 0) return set;
  const capacity = Math.max(2, Math.floor(plotW / 80));
  const stride = Math.max(1, Math.ceil(n / capacity));
  for (let i = n - 1; i >= 0; i -= stride) set.add(i);
  return set;
}

/** SVG 坐标保留 2 位，避免超长小数 */
function f(v: number): string {
  return String(Math.round(v * 100) / 100);
}
